import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { defaultMonthsRangeSettings, type TStatus } from "@package/grid-utils";
import { useGraphQLClient, useSubscribe } from "@package/graphql/react";
import type { GraphQLClient } from "@package/graphql";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";

import {
  GridSettings,
  GridSettingsInsert,
  GridSettingsQuery,
  GridSettingsSubscription,
  GridSettingsUpdate,
  SnapshotDateQuery,
} from "./queries";
import type { GridSettingsFragment } from "../../__generated__/gql/graphql";
import { GridSettingsThemeQuery, GridSettingsThemeUpdate } from "./theme";
import type { GridSettingsThemeQueryQuery, GridSettingsThemeUpdateMutation } from "../../__generated__/gql/graphql";
import { readFragment } from "../../graphql";
import { headerHeight } from "../../globals";
import { isMobile } from "../../shared/hooks";

import { isSeparatorDragActive } from "./separatorDragGate";
import { useUserId } from "../../context/auth/hooks";

export type TGridSettings = Omit<GridSettingsFragment, "enabled_snapshot_warnings"> & {
  enabled_snapshot_warnings: (typeof snapshot_warning_times)[number][];
  saved_colours?: string[];
  show_world_clock?: boolean | null;
  insert_page_position?: string | null;
  insert_column_position?: string | null;
  default_curve_column_width?: number | null;
  anomaly_sensitivity_level?: number | null;
  range_summary_dp?: number | null;
  range_summary_calc?: string | null;
};

export * from "./preferences";
export * from "./advanced";
const key = (userId: string) => ["grid_settings", userId];

const fetchOrCreateGridSettings = async (graphql: GraphQLClient, userId: string) => {
  // biome-ignore lint/suspicious/noExplicitAny: GraphQL generated types require this cast
  const result = (await graphql.execute(GridSettingsQuery as any, { userId })) as any;
  // biome-ignore lint/suspicious/noExplicitAny: GraphQL generated types require this cast
  const settings = readFragment(GridSettings as any, result.data?.grid_settings?.[0] as any) as
    | TGridSettings
    | null
    | undefined;

  if (!settings) {
    return (
      graphql
        // biome-ignore lint/suspicious/noExplicitAny: GraphQL generated types require this cast
        .execute(GridSettingsInsert as any, { object: { ...initGridSettings, user_id: userId } })
        // biome-ignore lint/suspicious/noExplicitAny: GraphQL generated types require this cast
        .then((x: any) => readFragment(GridSettings as any, x.data?.insert_grid_settings_one as any) as TGridSettings)
    );
  }
  return settings as TGridSettings;
};

const gridSettingsQueryOptions = (graphql: GraphQLClient, userId: string) =>
  queryOptions({
    queryKey: key(userId),
    queryFn: () => fetchOrCreateGridSettings(graphql, userId),
  });

export const useGridSettings = (userId: string) => {
  const graphql = useGraphQLClient();
  return useQuery(gridSettingsQueryOptions(graphql, userId));
};

// TODO: should this be an atom?
let PENDING_MUTATIONS = false;

const setPendingMutations = (value: boolean) => {
  PENDING_MUTATIONS = value;
};
const getPendingMutations = () => PENDING_MUTATIONS;

export const useSyncGridSettings = (userId: string | null) => {
  const qry = useQueryClient();

  useSubscribe({
    document: GridSettingsSubscription,
    variables: { userId: userId ?? "" },
    enabled: !!userId,
    next: (data) => {
      if (!userId) return;
      if (getPendingMutations() || isSeparatorDragActive() || !data.data?.grid_settings?.[0]) return;
      qry.setQueryData(key(userId), data.data?.grid_settings?.[0]);
    },
  });
};

export const useGridSettingsSelect = <T>(select: (s: TGridSettings | null | undefined) => T) => {
  const graphql = useGraphQLClient();
  const user_id = useUserId();
  return useQuery({
    ...gridSettingsQueryOptions(graphql, user_id),
    select,
  });
};

export const useGridSettingsStatusMap = () => {
  const statusMapStr = useGridSettingsSelect((x) => x?.status_map);
  return useMemo(() => (statusMapStr.data as Record<string, string> | null) ?? {}, [statusMapStr.data]);
};
export type GridSettingsStatusMap = ReturnType<typeof useGridSettingsStatusMap>;

export const useProductStatus = (productId: string) =>
  useGridSettingsSelect((x) => ((x?.status_map as Record<string, string> | null)?.[productId] ?? "listen") as TStatus);

export const useGridSettingsIncrementMap = () => {
  const incrementMapStr = useGridSettingsSelect((x) => x?.increment_map);
  return useMemo(() => (incrementMapStr.data as Record<string, number> | null) ?? {}, [incrementMapStr]);
};

export const useGridSettingsDisplayNameMap = () => {
  const displayNameMapStr = useGridSettingsSelect((x) => x?.display_name_map);
  return useMemo(() => (displayNameMapStr.data as Record<string, string> | null) ?? {}, [displayNameMapStr]);
};

export const useSavedColours = () => {
  const savedColoursQuery = useGridSettingsSelect((x) => x?.saved_colours);
  return useMemo(() => (savedColoursQuery.data as string[] | null) ?? [], [savedColoursQuery.data]);
};

const gridSettingUpdates: {
  timeout: ReturnType<typeof setTimeout> | null;
  updates: Partial<TGridSettings>;
} = {
  timeout: null,
  updates: {},
};

export const useUpdateGridSettings = () => {
  const graphql = useGraphQLClient();
  const qry = useQueryClient();
  const userId = useUserId();

  const mut = useMutation({
    networkMode: "always",
    mutationFn: () => {
      const values = gridSettingUpdates.updates;
      gridSettingUpdates.updates = {};
      // Null out the timeout reference so onSettled can tell whether a new
      // debounced update has been queued since this mutation fired.
      gridSettingUpdates.timeout = null;
      return graphql.execute(GridSettingsUpdate, {
        userId,
        values: { ...values, updated_at: new Date().toISOString() },
      });
    },
    onSettled: () => {
      setTimeout(() => {
        // If the user made another change while this mutation was in flight,
        // a new debounce timeout will be set. Skip cleanup in that case —
        // the next mutation's onSettled will handle it once the queue drains.
        // This prevents an early invalidateQueries from fetching stale DB data
        // before the pending mutation has written, which would flicker the UI.
        if (gridSettingUpdates.timeout) return;

        setPendingMutations(false);
        if (!isSeparatorDragActive()) {
          qry.invalidateQueries({ queryKey: key(userId) });
        }
      }, 300);
    },
  });

  const { mutate } = mut;

  const optimistic = useCallback(
    (values: Partial<TGridSettings>) => {
      qry.cancelQueries({ queryKey: key(userId) });
      setPendingMutations(true);
      gridSettingUpdates.updates = { ...gridSettingUpdates.updates, ...values };
      qry.setQueryData<TGridSettings>(key(userId), (old) => ({ ...old, ...values }) as TGridSettings);

      if (gridSettingUpdates.timeout) {
        clearTimeout(gridSettingUpdates.timeout);
      }

      gridSettingUpdates.timeout = setTimeout(() => mutate(), 300);
    },
    [qry, userId, mutate],
  );

  /** Update cache only (no debounced mutation). For live UI during separator drag. */
  const patchCache = useCallback(
    (values: Partial<TGridSettings>) => {
      qry.setQueryData<TGridSettings>(key(userId), (old) => ({ ...old, ...values }) as TGridSettings);
    },
    [qry, userId],
  );

  const reset = useCallback(() => optimistic(initGridSettings), [optimistic]);

  return {
    ...mut,
    optimistic,
    patchCache,
    reset,
  };
};

export const initGridSettings = {
  updated_at: new Date().toISOString(),
  extra_header_height: headerHeight,
  status_map: {},
  increment_map: {},
  display_name_map: {},

  commodity_parent_group: false,
  geographical_region: false,
  commodity_group: false,
  package: false,
  description: false,
  source: false,
  field_name: false,
  main_month_column_width: 70,

  adhoc_spreads_rows: 5,
  flash_cell_updates: false,
  enable_full_month_row_feature: false,
  qtr_switch: true,
  hlv_switch: true,
  season_switch: true,
  cal_switch: true,
  adhoc_spreads_switch: true,
  months: defaultMonthsRangeSettings,
  hlvs: 7,
  seasons: 6,
  season_current: false,
  hlv_current: false,
  cals: 4,
  qtr_current: true,
  cal_current: false,
  qtrs: 12,
  alternating_row_colours: true,
  hide_status_row: false,
  hide_eod: true,
  hide_broadcast: true,
  hide_private: false,
  hide_global: false,
  grid_scratchpad_switch: false,
  scratchpad_max_cols: 1,
  scratchpad_max_rows: 1,
  quarter_borders: true,
  sound: false,
  indicator_colour: "#ff0000",
  header_column_colour: "#1a4088",
  subheader_column_colour: "var(--header-column-colour)",
  broadcast_colour: "#ffffcc",
  hybrid_colour: "#fce3e3",
  listen_colour: "#b7d7f5",
  eod_colour: "#c6e0b4",
  local_colour: "#ededed",
  global_colour: "#33AA44",
  enabled_snapshot_warnings: [] as (typeof snapshot_warning_times)[number][],
  detailed_tooltip: false,
  display_negative_color: false,
  display_negative_bracket: false,
  show_world_clock: true,
  insert_page_position: "last",
  insert_column_position: "right_of_current",
  default_curve_column_width: 60,
  anomaly_sensitivity_level: 1,
  range_summary_dp: 2,
  range_summary_calc: "average",
  saved_colours: [] as string[],
} satisfies Omit<TGridSettings, "user_id">;

export const useShowWorldClock = () => useGridSettingsSelect((settings) => settings?.show_world_clock ?? true);

export const snapshot_warning_times = ["16:30 SIN", "12:30 LON", "16:30 LON", "14:30 NYC"] as const;

export type Theme = "light" | "dark" | "system";

const getSystemTheme = (): "light" | "dark" => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const resolveTheme = (theme: string | null | undefined): "light" | "dark" => {
  if (isMobile) {
    return getSystemTheme();
  }

  if (!theme || theme === "system") {
    return getSystemTheme();
  }

  return theme === "dark" ? "dark" : "light";
};

const themeKey = (userId: string) => ["grid_settings_theme", userId];

export const useTheme = () => {
  const graphql = useGraphQLClient();
  const userId = useUserId();

  const result = useQuery({
    queryKey: themeKey(userId),
    queryFn: async (): Promise<string | null> => {
      const response = await graphql.execute<GridSettingsThemeQueryQuery, { userId: string }>(GridSettingsThemeQuery, {
        userId,
      });
      const settings = response.data?.grid_settings?.[0] as { theme?: string | null } | undefined;
      return settings?.theme || null;
    },
    enabled: !!userId,
  });

  const storedTheme = result.data as Theme | null | undefined;
  const resolvedTheme = resolveTheme(storedTheme);

  return {
    theme: resolvedTheme,
    storedTheme: storedTheme as Theme | undefined,
    fetching: result.isLoading,
    error: result.error,
  };
};

export const useSetTheme = () => {
  const graphql = useGraphQLClient();
  const qry = useQueryClient();
  const userId = useUserId();

  const mutation = useMutation({
    mutationFn: (theme: Theme) => {
      if (isMobile) {
        return Promise.resolve(null);
      }

      return graphql.execute<GridSettingsThemeUpdateMutation, { userId: string; theme: string }>(
        GridSettingsThemeUpdate,
        {
          userId,
          theme: theme === "system" ? "system" : theme,
        },
      );
    },
    onSettled: () => {
      qry.invalidateQueries({ queryKey: themeKey(userId) });
    },
  });

  const { mutate } = mutation;

  return useCallback(
    (theme: Theme) => {
      if (!isMobile) {
        qry.setQueryData(themeKey(userId), theme);
      }
      mutate(theme);
    },
    [mutate, qry, userId],
  );
};

export function useSnapshotDateQuery() {
  const graphql = useGraphQLClient();
  return useQuery({
    queryKey: ["snapshot_date"],
    queryFn: () => {
      const today = dayjs();
      const twoWeeksAgo = today.subtract(2, "week");
      return graphql
        .execute(SnapshotDateQuery, {
          from: twoWeeksAgo.format("YYYY-MM-DD"),
          to: today.format("YYYY-MM-DD"),
        })
        .then((res) => {
          const snapshotDate = res?.data?.eod_entry?.[0]?.evaluation_date;
          if (!snapshotDate) return null;
          return dayjs(snapshotDate).format("YYYY-MM-DD");
        });
    },
  });
}
